import React, { useCallback, useEffect, useRef, useState } from "react";
import { Modal } from "react-bootstrap";
import Icon from "../utils/icons";
import { getAlerts, markAlertsRead, readAlertStream } from "../services/alerts";
import { playAlertSound, unlockAlertSound } from "../utils/alertSound";
import { enablePush, notificationPermission, notificationsSupported, turnOnNotifications } from "../utils/pushNotifications";

const RETRY_MS = [2000, 5000, 10000, 30000];

function timeAgo(date) {
  const seconds = Math.max(0, (Date.now() - new Date(date).getTime()) / 1000);
  if (seconds < 60) return "Just now";
  if (seconds < 3600) return `${Math.floor(seconds / 60)} min ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)} h ago`;
  return new Date(date).toLocaleDateString("en-IN", { day: "numeric", month: "short" });
}

// The signed-in shopper's bell in the store header: news about their own orders (placed, packed, shipped, delivered,
// payment received), arriving live with a chime. In the background it shows a notification: a push notification from
// the service worker once notifications are on, otherwise a browser notification. Opening an alert shows that order.
// onOrderChanged(orderId): an update arrived, so the order list can show the order's new status straight away
export default function ShopperAlerts({ userId, notify, onOpenOrder, onOrderChanged }) {
  const [alerts, setAlerts] = useState(null);
  const [unread, setUnread] = useState(0);
  const [open, setOpen] = useState(false);
  const [permission, setPermission] = useState(notificationPermission);
  const pushOn = useRef(false);

  const load = useCallback(() => getAlerts().then((data) => { setAlerts(data.alerts); setUnread(data.unread); }).catch(() => {}), []);

  const arrived = useRef(null);
  arrived.current = (alert) => {
    setAlerts((list) => (list && list.some((a) => a.id === alert.id) ? list : [alert, ...(list || [])].slice(0, 50)));
    setUnread((n) => n + 1);
    if (alert.orderId && onOrderChanged) onOrderChanged(alert.orderId);
    playAlertSound();
    notify(`🔔 ${alert.title}`);
    if (document.hidden && !pushOn.current && notificationsSupported() && Notification.permission === "granted") {
      const note = new Notification(alert.title, { body: alert.body, tag: `order-${alert.orderId}` });
      note.onclick = () => { window.focus(); onOpenOrder(alert.orderId); note.close(); };
    }
  };

  useEffect(() => {
    const unlock = () => unlockAlertSound();
    window.addEventListener("pointerdown", unlock);
    window.addEventListener("keydown", unlock);
    return () => { window.removeEventListener("pointerdown", unlock); window.removeEventListener("keydown", unlock); };
  }, []);

  // Signed in: the alerts, then the live stream (reconnecting after a drop and catching up on what was missed)
  useEffect(() => {
    if (!userId) return undefined;
    load();
    if (notificationPermission() === "granted") enablePush().then((on) => { pushOn.current = on; }).catch(() => {});
    const controller = new AbortController();
    let attempt = 0;
    let timer = null;
    const connect = () => {
      readAlertStream((event, data) => {
        if (event === "ready") { attempt = 0; setUnread(data.unread); } else if (event === "alert") arrived.current(data);
      }, controller.signal)
        .catch((err) => err)
        .then((err) => {
          if (controller.signal.aborted || (err && err.status === 401)) return;
          timer = setTimeout(() => { load(); connect(); }, RETRY_MS[Math.min(attempt, RETRY_MS.length - 1)]);
          attempt += 1;
        });
    };
    connect();
    return () => { controller.abort(); clearTimeout(timer); };
  }, [userId, load]);

  if (!userId) return null;

  const pick = (alert) => {
    setOpen(false);
    if (!alert.read) {
      setAlerts((list) => list.map((a) => (a.id === alert.id ? { ...a, read: true } : a)));
      markAlertsRead([alert.id]).then((data) => setUnread(data.unread)).catch(() => {});
    }
    if (alert.orderId) onOpenOrder(alert.orderId);
  };
  const markAll = () => {
    setAlerts((list) => (list || []).map((a) => ({ ...a, read: true })));
    setUnread(0);
    markAlertsRead().catch(() => {});
  };
  const turnOn = async () => {
    const result = await turnOnNotifications();
    setPermission(result);
    pushOn.current = result === "granted";
    notify(result === "granted" ? "Notifications are on. We'll let you know about your orders." : "Notifications are blocked. Allow them in your browser's site settings.");
  };

  return (
    <>
      <button type="button" className={`shopperBell ${unread ? "hasUnread" : ""}`} onClick={() => { setOpen(true); load(); }} aria-label={unread ? `Order updates, ${unread} new` : "Order updates"} title="Order updates">
        <Icon.BellFill size={19} />
        {unread > 0 && <span className="shopperBellCount">{unread > 9 ? "9+" : unread}</span>}
      </button>
      <Modal show={open} onHide={() => setOpen(false)} centered dialogClassName="shopperAlertsModal">
        <Modal.Header closeButton>
          <Modal.Title>Order updates</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <div className="shopperAlertsActions">
            {notificationsSupported() && permission === "default" && (
              <button type="button" className="green" onClick={turnOn}><Icon.BellFill /> Get notified about my orders</button>
            )}
            {permission === "granted" && <span className="shopperAlertsOn">✓ Notifications are on</span>}
            {unread > 0 && <button type="button" className="textBtn" onClick={markAll}>Mark all as read</button>}
          </div>
          {alerts === null ? (
            <p className="shopperAlertsEmpty">Loading…</p>
          ) : !alerts.length ? (
            <p className="shopperAlertsEmpty">No updates yet. When you place an order, its news shows up here.</p>
          ) : (
            <ul className="shopperAlertsList">
              {alerts.map((alert) => (
                <li key={alert.id}>
                  <button type="button" className={alert.read ? "" : "unread"} onClick={() => pick(alert)}>
                    <span className="shopperAlertsIcon" aria-hidden="true"><Icon.BoxSeam /></span>
                    <span className="shopperAlertsText"><b>{alert.title}</b>{alert.body && <small>{alert.body}</small>}<em>{timeAgo(alert.createdAt)}</em></span>
                    {!alert.read && <span className="shopperAlertsDot" aria-label="New" />}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>
    </>
  );
}
